import React, { useEffect, useState } from 'react'
import { Row, Col, Typography, Card, Button, message, Space } from 'antd'
import {
	PlusOutlined,
	DownloadOutlined,
	UploadOutlined,
	DatabaseOutlined,
} from '@ant-design/icons'
import PricingFilters from 'component/Pricing/PricingFilters'
import PricingTable from 'component/Pricing/PricingTable'
import PricingForm from 'component/Pricing/PricingForm'
import { MessagingPrice, PricingFilters as PricingFiltersType } from 'types'
import pricingService from 'api/pricing/pricingService'

const { Title } = Typography

/**
 * Main Pricing Management Page
 * Provides interface for managing vendor, country, and currency-based pricing
 */
const Pricing: React.FC = () => {
	const [pricingData, setPricingData] = useState<MessagingPrice[]>([])
	const [loading, setLoading] = useState(false)
	const [totalCount, setTotalCount] = useState(0)
	const [currentPage, setCurrentPage] = useState(1)
	const [pageSize, setPageSize] = useState(20)
	const [filters, setFilters] = useState<PricingFiltersType>({})
	const [formVisible, setFormVisible] = useState(false)
	const [editingRecord, setEditingRecord] = useState<MessagingPrice | null>(null)

	/**
	 * Fetch pricing data based on current filters and pagination
	 */
	const fetchPricingData = async () => {
		setLoading(true)
		try {
			const response = await pricingService.getPricing({
				...filters,
				page: currentPage,
				size: pageSize,
			})
			setPricingData(response.records || [])
			setTotalCount(response.totalCount || 0)
		} catch (error) {
			message.error('Failed to fetch pricing data')
		} finally {
			setLoading(false)
		}
	}

	/**
	 * Handle filter changes
	 */
	const handleFiltersChange = (newFilters: PricingFiltersType) => {
		setFilters(newFilters)
		setCurrentPage(1) // Reset to first page when filters change
	}

	/**
	 * Handle pagination change
	 */
	const handlePaginationChange = (page: number, size?: number) => {
		setCurrentPage(page)
		if (size && size !== pageSize) {
			setPageSize(size)
		}
	}

	/**
	 * Handle adding new pricing entry
	 */
	const handleAddNew = () => {
		setEditingRecord(null)
		setFormVisible(true)
	}

	/**
	 * Handle editing existing pricing entry
	 */
	const handleEdit = (record: MessagingPrice) => {
		setEditingRecord(record)
		setFormVisible(true)
	}

	/**
	 * Handle form submission (create or update)
	 */
	const handleFormSubmit = async () => {
		await fetchPricingData()
		setFormVisible(false)
		setEditingRecord(null)
	}

	/**
	 * Handle form cancellation
	 */
	const handleFormCancel = () => {
		setFormVisible(false)
		setEditingRecord(null)
	}

	/**
	 * Handle deleting pricing entry
	 */
	const handleDelete = async (id: string) => {
		try {
			await pricingService.deletePricing(id)
			message.success('Pricing entry deleted successfully')
			await fetchPricingData()
		} catch (error) {
			message.error('Failed to delete pricing entry')
		}
	}

	/**
	 * Handle bulk operations
	 */
	const handleBulkUpdate = async (
		updates: Array<{ _id: string; data: any }>
	) => {
		try {
			await pricingService.bulkUpdatePricing(updates)
			message.success('Bulk update completed successfully')
			await fetchPricingData()
		} catch (error) {
			message.error('Failed to perform bulk update')
		}
	}

	/**
	 * Seed sample pricing entries when DB is empty
	 */
	const handleSeedSampleData = async () => {
		try {
			setLoading(true)
			await pricingService.seedSampleData()
			message.success('Sample pricing data created')
			await fetchPricingData()
		} catch (error) {
			message.error('Failed to seed sample pricing data')
		} finally {
			setLoading(false)
		}
	}

	/**
	 * Handle CSV export
	 */
	const handleExport = async () => {
		try {
			const blob = await pricingService.exportPricing(filters)
			const url = window.URL.createObjectURL(blob)
			const link = document.createElement('a')
			link.href = url
			link.download = `pricing-export-${
				new Date().toISOString().split('T')[0]
			}.csv`
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)
			window.URL.revokeObjectURL(url)
			message.success('Pricing data exported successfully')
		} catch (error) {
			message.error('Failed to export pricing data')
		}
	}

	/**
	 * Handle CSV import
	 */
	const handleImport = async (file: File) => {
		try {
			await pricingService.importPricing(file)
			message.success('Pricing data imported successfully')
			await fetchPricingData()
		} catch (error) {
			message.error('Failed to import pricing data')
		}
	}

	/**
	 * Fetch data on component mount and when dependencies change
	 */
	useEffect(() => {
		fetchPricingData()
	}, [currentPage, pageSize, filters])

	return (
		<Row gutter={[16, 16]}>
			{/* Header */}
			<Col span={24}>
				<Card>
					<Row justify='space-between' align='middle'>
						<Col>
							<Title level={2} style={{ margin: 0 }}>
								Pricing Management
							</Title>
							<Typography.Text type='secondary'>
								Manage vendor, country, and currency-based pricing
							</Typography.Text>
						</Col>
						<Col>
							<Space>
								<Button
									icon={<UploadOutlined />}
									onClick={() => {
										const input = document.createElement('input')
										input.type = 'file'
										input.accept = '.csv'
										input.onchange = e => {
											const file = (e.target as HTMLInputElement).files?.[0]
											if (file) handleImport(file)
										}
										input.click()
									}}
								>
									Import CSV
								</Button>
								<Button icon={<DownloadOutlined />} onClick={handleExport}>
									Export CSV
								</Button>
								{totalCount === 0 && (
									<Button icon={<DatabaseOutlined />} onClick={handleSeedSampleData}>
										Seed Sample Data
									</Button>
								)}
								<Button type='primary' icon={<PlusOutlined />} onClick={handleAddNew}>
									Add Pricing
								</Button>
							</Space>
						</Col>
					</Row>
				</Card>
			</Col>

			{/* Filters */}
			<Col span={24}>
				<PricingFilters
					onFiltersChange={handleFiltersChange}
					onRefresh={fetchPricingData}
				/>
			</Col>

			{/* Pricing Table */}
			<Col span={24}>
				<PricingTable
					data={pricingData}
					loading={loading}
					totalCount={totalCount}
					currentPage={currentPage}
					pageSize={pageSize}
					onPaginationChange={handlePaginationChange}
					onEdit={handleEdit}
					onDelete={handleDelete}
					onBulkUpdate={handleBulkUpdate}
				/>
			</Col>

			{/* Pricing Form */}
			<PricingForm
				visible={formVisible}
				editingRecord={editingRecord}
				onSubmit={handleFormSubmit}
				onCancel={handleFormCancel}
			/>
		</Row>
	)
}

export default Pricing
